import useUserStore from "@/store/user.store.ts";
import PWA from "@/ui/shared/PWA.tsx";
import PWAInstaller from "@/ui/shared/PWAInstaller.tsx";
import { createTheme, MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { notifications } from "@mantine/notifications";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import axios from "axios";
import { PropsWithChildren, useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router";

const theme = createTheme({
  primaryColor: "violet",
  defaultRadius: "md",
  cursorType: "pointer",
});

function App({ children }: PropsWithChildren) {
  const [loadingCount, setLoadingCount] = useState(0);
  const { logout, updateLoading } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const message = searchParams.get("payment");

    if (!message) {
      return;
    }

    if (message === "success") {
      notifications.show({
        title: "Success!",
        message:
          "Your payment has been processed successfully. Your credits will be added to your account as soon as possible.",
        color: "green",
      });
      setSearchParams({});
    }
  }, [searchParams]);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            if (axios.isAxiosError(error) && error.response) {
              notifications.show({
                title: "Whooops!",
                message: error.response.data.message,
                color: "red",
              });
            }

            if (axios.isAxiosError(error) && error.status === 401) {
              logout();
            }
          },
        }),
        mutationCache: new MutationCache({
          onMutate: () => {
            // setLoadingCount((prev) => prev + 1);
          },
          onError: (error) => {
            if (axios.isAxiosError(error) && error.response) {
              notifications.show({
                title: "Whooops!",
                message: error.response.data.message,
                color: "red",
              });
            }
          },
          onSettled: () => {
            setLoadingCount((prev) => prev - 1);
          },
          onSuccess: () => {
            queryClient.invalidateQueries();
          },
        }),
      }),
  );

  useEffect(() => {
    const loadingEvent = (e: CustomEvent) => {
      if (e.detail) {
        setLoadingCount((prev) => prev + 1);
      } else {
        setLoadingCount((prev) => prev - 1);
      }
    };

    document.addEventListener("axios:loading", loadingEvent as EventListener);

    return () => {
      document.removeEventListener(
        "axios:loading",
        loadingEvent as EventListener,
      );
    };
  }, []);

  const memoizedNotifications = useMemo(
    () => <Notifications position="top-right" />,
    [],
  );

  useEffect(() => {
    updateLoading(loadingCount > 0);
  }, [loadingCount, updateLoading]);

  return (
    <div className="min-h-dvh relative">
      <QueryClientProvider client={queryClient}>
        <MantineProvider defaultColorScheme="auto" theme={theme}>
          {memoizedNotifications}
          {children}
          <PWA />
          <PWAInstaller />
        </MantineProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
